import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getBlogImageForPost } from '../data/blogImages.js';
import { fallbackBlogPosts, mergeRebuiltFallbackPosts } from '../data/fallbackBlogPosts';
import { fetchBlogPosts, isStrapiConfigured } from '../lib/strapiBlog';

const {
  FiCalendar,
  FiUser,
  FiArrowRight,
  FiHeart,
  FiShield,
  FiUsers,
  FiHome,
  FiMapPin,
  FiBriefcase,
  FiFileText,
  FiClipboard,
  FiMoon,
} = FiIcons;

const categoryIcons = {
  'Dementia Care': FiShield,
  'Home Care': FiHome,
  'Personal Care': FiHeart,
  'Family Support': FiUsers,
  'Health & Wellness': FiHeart,
  'Live-in Care': FiHome,
  'Live-in & Night Care': FiMoon,
  'Choosing Care': FiClipboard,
  'Funding & Planning': FiFileText,
  'Local Home Care': FiMapPin,
  'Home Safety': FiShield,
  'Hospital to Home': FiHeart,
  'Care Careers': FiBriefcase,
  Companionship: FiUsers,
  'Wellbeing at Home': FiHeart,
  'Care Advice': FiHome,
};

const getCategoryIcon = (category) => categoryIcons[category] || FiHome;

const getCategoryCounts = (posts) => {
  const counts = posts.reduce(
    (acc, post) => {
      const category = post.category || 'Care Advice';
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    },
    { 'All Posts': posts.length }
  );

  return Object.entries(counts).map(([name, count]) => ({
    name,
    count,
    icon: name === 'All Posts' ? FiHome : getCategoryIcon(name),
  }));
};

const Blog = () => {
  const [posts, setPosts] = useState(fallbackBlogPosts);
  const [selectedCategory, setSelectedCategory] = useState('All Posts');
  const [isLoading, setIsLoading] = useState(isStrapiConfigured);

  useEffect(() => {
    let mounted = true;

    const loadPosts = async () => {
      if (!isStrapiConfigured) return;

      try {
        const strapiPosts = await fetchBlogPosts();
        if (mounted && strapiPosts.length > 0) {
          setPosts(mergeRebuiltFallbackPosts(strapiPosts));
        }
      } catch (error) {
        console.warn('Unable to load Strapi blog posts. Falling back to local content.', error);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    loadPosts();

    return () => {
      mounted = false;
    };
  }, []);

  const categories = useMemo(() => getCategoryCounts(posts), [posts]);
  const featuredPost = posts.find((post) => post.featured) || posts[0];
  const visiblePosts = posts.filter((post) => {
    const matchesCategory =
      selectedCategory === 'All Posts' || post.category === selectedCategory;
    return matchesCategory && post.slug !== featuredPost?.slug;
  });
  const popularTopics = categories
    .filter((category) => category.name !== 'All Posts')
    .slice(0, 4)
    .map((category) => ({
      title: category.name,
      count: `${category.count} ${category.count === 1 ? 'article' : 'articles'}`,
      icon: category.icon,
    }));

  return (
    <>
      <Helmet>
        <title>UK Home Care Blog - Odara Care | Family Guides & Care Planning</title>
        <meta
          name="description"
          content="UK home care guides for families comparing care at home, CQC-registered providers, funding questions, respite, companionship, dementia support and care careers."
        />
        <meta
          name="keywords"
          content="UK home care blog, home care provider checklist, care at home guidance, CQC home care, care needs assessment, family care support, care jobs"
        />
      </Helmet>

      <section className="relative bg-gradient-to-br from-blue-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Care <span className="text-blue-600">Insights</span> &{' '}
              <span className="text-blue-600">Resources</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Practical UK guides to help families compare home care providers, prepare for care
              conversations, understand funding routes and plan safer support at home.
            </p>
          </motion.div>
        </div>
      </section>

      {featuredPost && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                Featured Article
              </h2>
              <article className="bg-gradient-to-r from-blue-50 to-white rounded-2xl overflow-hidden shadow-lg">
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <div className="mb-4">
                      <span className="inline-block bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                        {featuredPost.category}
                      </span>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">
                      {featuredPost.title}
                    </h3>
                    <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                      {featuredPost.excerpt}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 mb-6">
                      <span className="inline-flex items-center">
                        <SafeIcon icon={FiUser} className="w-4 h-4 mr-2" />
                        {featuredPost.author}
                      </span>
                      <span className="inline-flex items-center">
                        <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2" />
                        {featuredPost.date}
                      </span>
                      <span>{featuredPost.readTime}</span>
                    </div>
                    <Link
                      to={`/blog/${featuredPost.slug}`}
                      className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold group"
                    >
                      Read Full Article
                      <SafeIcon
                        icon={FiArrowRight}
                        className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform"
                      />
                    </Link>
                  </div>
                  <div className="lg:order-first">
                    <img
                      src={featuredPost.image}
                      alt={featuredPost.imageAlt || featuredPost.title}
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = getBlogImageForPost(
                          featuredPost.category,
                          featuredPost.title
                        );
                      }}
                      className="w-full h-64 lg:h-full object-cover"
                    />
                  </div>
                </div>
              </article>
            </motion.div>
          </div>
        </section>
      )}

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <motion.aside
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-1"
            >
              <div className="bg-white p-6 rounded-xl shadow-sm sticky top-28">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Categories</h3>
                <div className="space-y-3">
                  {categories.map((category) => (
                    <button
                      key={category.name}
                      type="button"
                      onClick={() => setSelectedCategory(category.name)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors text-left ${
                        selectedCategory === category.name
                          ? 'bg-blue-50 text-blue-900'
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      <span className="flex items-center">
                        <SafeIcon icon={category.icon} className="w-4 h-4 text-blue-600 mr-3" />
                        <span>{category.name}</span>
                      </span>
                      <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {category.count}
                      </span>
                    </button>
                  ))}
                </div>

                <div className="mt-8 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2">Need Help Choosing Care?</h4>
                  <p className="text-sm text-blue-800 mb-3">
                    Talk to Odara Care about visit patterns, family communication and local
                    availability.
                  </p>
                  <Link
                    to="/request-care"
                    className="inline-flex text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Contact Us
                  </Link>
                </div>
              </div>
            </motion.aside>

            <div className="lg:col-span-3">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="mb-8"
              >
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  {selectedCategory === 'All Posts' ? 'Latest Articles' : selectedCategory}
                </h2>
                <p className="text-gray-600">
                  Research-backed, practical topics for families and people considering care work
                </p>
              </motion.div>

              {isLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {[0, 1, 2, 3].map((item) => (
                    <div key={item} className="bg-white rounded-xl shadow-sm overflow-hidden">
                      <div className="h-48 bg-gray-200 animate-pulse" />
                      <div className="p-6 space-y-4">
                        <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                        <div className="h-6 bg-gray-200 rounded animate-pulse" />
                        <div className="h-16 bg-gray-100 rounded animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {visiblePosts.map((post, index) => (
                    <motion.article
                      key={post.id}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden group"
                    >
                      <Link to={`/blog/${post.slug}`} className="block">
                        <div className="aspect-w-16 aspect-h-9">
                          <img
                            src={post.image}
                            alt={post.imageAlt || post.title}
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src = getBlogImageForPost(
                                post.category,
                                post.title
                              );
                            }}
                            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="p-6">
                          <div className="mb-3">
                            <span className="inline-block bg-gray-100 text-gray-800 text-xs font-medium px-2 py-1 rounded">
                              {post.category}
                            </span>
                          </div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                            {post.title}
                          </h3>
                          <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500">
                            <span className="flex items-center">
                              <SafeIcon icon={FiUser} className="w-4 h-4 mr-1" />
                              {post.author}
                            </span>
                            <span className="flex items-center gap-3">
                              <span className="flex items-center">
                                <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-1" />
                                {post.date}
                              </span>
                              <span>{post.readTime}</span>
                            </span>
                          </div>
                        </div>
                      </Link>
                    </motion.article>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl font-bold text-white mb-4">Stay Updated</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Subscribe to our newsletter for the latest care insights, tips, and resources
              delivered to your inbox.
            </p>
            <div className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 rounded-lg border-0 focus:ring-2 focus:ring-blue-300"
                />
                <button className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200">
                  Subscribe
                </button>
              </div>
              <p className="text-blue-100 text-sm mt-3">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {popularTopics.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Popular Topics</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Explore our most popular care topics and resources
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {popularTopics.map((topic, index) => (
                <button
                  key={topic.title}
                  type="button"
                  onClick={() => {
                    setSelectedCategory(topic.title);
                    window.scrollTo({ top: 760, behavior: 'smooth' });
                  }}
                  className="text-left"
                >
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow duration-300 group h-full"
                  >
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-600 transition-colors duration-300">
                      <SafeIcon
                        icon={topic.icon}
                        className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors duration-300"
                      />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{topic.title}</h3>
                    <p className="text-gray-600">{topic.count}</p>
                  </motion.div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default Blog;
